import { useState, useEffect } from 'react';

const BACKEND = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const C = {
  navy:   '#1E293B',
  gold:   '#BA7517',
  border: '#e8eaef',
  muted:  '#6b7280',
  text:   '#1a1f2e',
  card:   '#ffffff',
  green:  '#059669',
};

export default function MediaStore() {
  const [assets, setAssets]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setType]     = useState('all');
  const [editing, setEditing]     = useState({}); // id -> field -> value
  const [saving, setSaving]       = useState({}); // id -> bool
  const [copied, setCopied]       = useState(null);

  useEffect(() => { fetchAssets(); }, [search, typeFilter]);

  async function fetchAssets() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (search.trim())        params.set('q', search.trim());
      const res = await fetch(`${BACKEND}/api/assets?${params}`, {
        headers: { 'x-api-key': API_KEY }
      });
      const data = await res.json();
      setAssets(Array.isArray(data) ? data : []);
    } catch(e) {
      console.error('[MediaStore] fetch error', e);
    } finally {
      setLoading(false);
    }
  }

  async function patchAsset(id, fields) {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await fetch(`${BACKEND}/api/assets/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify(fields)
      });
      setAssets(a => a.map(x => x.id === id ? { ...x, ...fields } : x));
      setEditing(e => { const n = { ...e }; delete n[id]; return n; });
    } catch(e) {
      console.error('[MediaStore] patch error', e);
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  }

  function copyUrl(url, id) {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  function sendToQueue(asset) {
    const item = {
      id: 'q_' + Date.now(),
      title: asset.title || 'Asset from Media Store',
      platform: asset.platform || 'LinkedIn',
      content_type: asset.type === 'video' ? 'Video Post' : 'Image Post',
      output_url: asset.url,
      status: 'completed',
      source_text: '',
    };
    const existing = JSON.parse(localStorage.getItem('sh_ci_queue') || '[]');
    localStorage.setItem('sh_ci_queue', JSON.stringify([item, ...existing]));
    alert('✅ Sent to Queue!');
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>🗄️ Media Store</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{assets.length} assets · all generated images & videos</div>
        </div>
        <button onClick={fetchAssets} style={{ padding: '7px 16px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: `1px solid ${C.border}`, background: '#f8fafc', color: C.text, cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by title or tag..."
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none' }}
        />
        {['all','image','video'].map(t => (
          <button key={t} onClick={() => setType(t)}
            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${typeFilter === t ? C.navy : C.border}`,
              background: typeFilter === t ? C.navy : '#f8fafc',
              color: typeFilter === t ? '#fff' : C.muted }}>
            {t === 'all' ? 'All' : t === 'image' ? '🖼 Images' : '🎬 Videos'}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Loading assets...</div>
      ) : assets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🗄️</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No assets yet</div>
          <div style={{ fontSize: 13 }}>Generate an image or video in Media Studio and click Save to Library</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {assets.filter(a => a.id !== 'test-001').map(asset => (
            <div key={asset.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {/* Thumbnail */}
              <div style={{ position: 'relative', height: 180, background: '#f1f5f9', overflow: 'hidden' }}>
                {asset.type === 'video' ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>🎬</div>
                ) : (
                  <img src={asset.thumbnail_url || asset.url} alt={asset.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                <div style={{ position: 'absolute', top: 8, left: 8, background: asset.type === 'video' ? '#7c3aed' : C.navy,
                  color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20 }}>
                  {asset.type === 'video' ? '🎬 VIDEO' : '🖼 IMAGE'}
                </div>
                {asset.platform && (
                  <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)',
                    color: '#fff', fontSize: 10, padding: '3px 8px', borderRadius: 20 }}>
                    {asset.platform}
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4, lineHeight: 1.4,
                  overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {asset.title || 'Untitled'}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
                  {new Date(asset.created_at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}
                </div>

                {/* Social URLs */}
                {asset.type === 'image' && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>📌 Pinterest URL</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={editing[asset.id]?.pinterest_url ?? asset.pinterest_url ?? ''}
                        onChange={e => setEditing(ed => ({ ...ed, [asset.id]: { ...ed[asset.id], pinterest_url: e.target.value }}))}
                        placeholder="Paste Pinterest URL..."
                        style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 11, outline: 'none' }}
                      />
                      {editing[asset.id]?.pinterest_url !== undefined && (
                        <button onClick={() => patchAsset(asset.id, { pinterest_url: editing[asset.id].pinterest_url })}
                          disabled={saving[asset.id]}
                          style={{ padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: 'none',
                            background: C.green, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {saving[asset.id] ? '...' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {asset.type === 'video' && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>▶️ YouTube URL</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={editing[asset.id]?.youtube_url ?? asset.youtube_url ?? ''}
                        onChange={e => setEditing(ed => ({ ...ed, [asset.id]: { ...ed[asset.id], youtube_url: e.target.value }}))}
                        placeholder="Paste YouTube URL..."
                        style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 11, outline: 'none' }}
                      />
                      {editing[asset.id]?.youtube_url !== undefined && (
                        <button onClick={() => patchAsset(asset.id, { youtube_url: editing[asset.id].youtube_url })}
                          disabled={saving[asset.id]}
                          style={{ padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: 'none',
                            background: C.green, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {saving[asset.id] ? '...' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {asset.type === 'video' && (
                  <button onClick={() => patchAsset(asset.id, { approved_for_youtube: !asset.approved_for_youtube })}
                    disabled={saving[asset.id]}
                    style={{ width: '100%', padding: '7px 0', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 8,
                      border: `1px solid ${asset.approved_for_youtube ? C.green : C.border}`,
                      background: asset.approved_for_youtube ? '#f0fdf4' : '#f8fafc',
                      color: asset.approved_for_youtube ? C.green : C.text }}>
                    {asset.youtube_scheduled ? '📅 Scheduled for YouTube' : asset.approved_for_youtube ? '✅ Approved for YouTube' : '👍 Approve for YouTube'}
                  </button>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button onClick={() => copyUrl(asset.url, asset.id)}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${C.border}`, background: copied === asset.id ? '#f0fdf4' : '#f8fafc',
                      color: copied === asset.id ? C.green : C.text }}>
                    {copied === asset.id ? '✅ Copied!' : '📋 Copy URL'}
                  </button>
                  <button onClick={() => sendToQueue(asset)}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: 'none', background: C.navy, color: '#fff' }}>
                    📤 Send to Queue
                  </button>
                  <a href={asset.url} target="_blank" rel="noopener noreferrer"
                    style={{ padding: '7px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${C.border}`, background: '#f8fafc', color: C.muted, textDecoration: 'none' }}>
                    ↗
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
